import { useEffect, useRef, useState } from 'react';

interface ClosingSectionProps {
  onOpenBooking: () => void;
  onOpenDealer: () => void;
}

export default function ClosingSection({ onOpenBooking, onOpenDealer }: ClosingSectionProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="closing" ref={ref} className="py-16 md:py-24 bg-[#050608] relative overflow-hidden">
      {/* Glossy top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1E6BFF] to-transparent" />

      {/* Blue radial glow center */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-[#1E6BFF]/12 blur-[130px]" />
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'linear-gradient(rgba(90,154,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(90,154,255,0.8) 1px, transparent 1px)', backgroundSize: '80px 80px'}} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 text-center">
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-[#0A1535] border border-[#1A2F60] rounded-full px-4 py-2 mb-8">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-[#1E6BFF] text-sm" />
            </div>
            <span className="text-[#5A9AFF] text-xs md:text-sm font-bold">先行者利益 - 今だけの機会</span>
          </div>

          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#E8EFFF] mb-6 md:mb-8 leading-tight">
            まだ<span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">34施設</span>しかない。<br />
            これが、今動く理由。
          </h2>

          <p className="text-[#C0CBDD] text-base md:text-xl leading-relaxed mb-8 md:mb-10 max-w-2xl mx-auto">
            フィジオショットは全国でまだ34施設のみの導入。
            プロアスリートが使うリカバリー技術を、今すぐあなたの日常に。
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5 mb-10 md:mb-12">
            {[
              {
                icon: 'ri-map-pin-line',
                title: '近隣施設が少ない今',
                desc: '導入施設数が少ないうちが最も早く体験できる機会',
              },
              {
                icon: 'ri-vip-crown-line',
                title: 'プロと同じケアを',
                desc: 'Jリーガー・プロ野球選手と同じリカバリーが今すぐ可能',
              },

            ].map((item) => (
              <div key={item.title} className="bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-5 transition-all hover:shadow-[0_0_20px_rgba(30,107,255,0.1)]">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] rounded-xl mx-auto mb-3">
                  <i className={`${item.icon} text-[#1E6BFF] text-xl`} />
                </div>
                <h4 className="text-[#E8EFFF] font-bold text-sm mb-2">{item.title}</h4>
                <p className="text-[#D0DDF0] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-8">
            <button
              onClick={onOpenBooking}
              className="group px-8 md:px-10 py-4 md:py-5 bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] hover:from-[#3D8FFF] hover:to-[#1A5FDD] text-white font-black text-base md:text-xl rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-[0_0_40px_rgba(30,107,255,0.6)] border-t border-[#5A9AFF]/30"
            >
              <span>今すぐ施術を予約する</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={onOpenDealer}
              className="px-8 md:px-10 py-4 md:py-5 border border-[#1E6BFF]/50 text-[#5A9AFF] hover:bg-[#1E6BFF]/10 font-bold text-base md:text-xl rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              施設への導入を相談する
            </button>
          </div>

          <div className="flex items-center justify-center gap-6 text-[#D0DDF0] text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-[#1E6BFF] text-sm" />
              </div>
              <span>無料相談受付中</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-[#1E6BFF] text-sm" />
              </div>
              <span>返信は通常24時間以内</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
